import { IFacilityInfo } from '../FacilityTable/FacilityItem/FacilityItem'
import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import { formatInTimeZone } from 'date-fns-tz'
import { useIsMobile } from 'hooks/useIsMobile'
import { useSuppressNotificationSandwich } from 'hooks/useSuppressNotificationSandwich'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Marker } from 'react-map-gl'
import Modal from 'react-modal'
import { TOKEN_MAPBOX } from 'utils/constants'
import { IMapboxElement, IRobotInfo } from 'utils/interfaces'
import { NOTIFICATION_SANDWICH_SUPPRESS } from 'utils/notificationSandwichGate'
import { getVendorDisplayName } from 'utils/vendorDisplayName'

import style from './AddFacilityModal.module.css'

const FACILITY_TYPE_OPTIONS: IOption[] = [
  { value: 'commercial', text: 'Commercial & Institutional' },
  { value: 'educational', text: 'Educational' },
  { value: 'healthcare', text: 'Healthcare' },
  { value: 'industrial', text: 'Industrial / Manufacturing' },
  { value: 'public', text: 'Public / Government' },
  { value: 'hospitality', text: 'Hotels & Hospitality' },
  { value: 'residential', text: 'Residential' },
  { value: 'financial', text: 'Financial' },
  { value: 'parking', text: 'Parking' },
  { value: 'warehouse', text: 'Warehouse' },
  { value: 'other', text: 'Other' },
]

interface IAddFacilityModal {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    name: string
    facility_type: string
    street_address?: string
    city?: string
    country?: string
    postal_code?: string
    timezone?: string
  }) => void
  className?: string
  robots: IRobotInfo[]
  facilityExisting?: IFacilityInfo
}

let TIMEZONE_OPTIONS: IOption[] = []
try {
  // @ts-ignore
  if (Intl && Intl.supportedValuesOf) {
    // @ts-ignore
    TIMEZONE_OPTIONS = Intl.supportedValuesOf('timeZone').map(tz => {
      try {
        const offset = formatInTimeZone(new Date(), tz, 'xxx')
        return { value: tz, text: `(UTC${offset}) ${tz}` }
      } catch (err) {
        return { value: tz, text: tz }
      }
    })
  }
} catch (e) {
  // Fallback if not supported
  TIMEZONE_OPTIONS = [
    { value: 'UTC', text: '(UTC+00:00) UTC' },
    { value: 'America/New_York', text: '(UTC-05:00) America/New_York' },
    { value: 'Europe/London', text: '(UTC+00:00) Europe/London' },
    { value: 'Europe/Paris', text: '(UTC+01:00) Europe/Paris' },
    { value: 'Asia/Tokyo', text: '(UTC+09:00) Asia/Tokyo' },
  ]
}

TIMEZONE_OPTIONS.sort((a, b) => {
  const offsetA = String(a.text).match(/UTC([+-]\d{2}):(\d{2})/)
  const offsetB = String(b.text).match(/UTC([+-]\d{2}):(\d{2})/)
  if (offsetA && offsetB) {
    const valA = parseInt(offsetA[1]) * 60 + (parseInt(offsetA[1]) < 0 ? -1 : 1) * parseInt(offsetA[2])
    const valB = parseInt(offsetB[1]) * 60 + (parseInt(offsetB[1]) < 0 ? -1 : 1) * parseInt(offsetB[2])
    if (valA !== valB) return valA - valB
  }
  return String(a.value).localeCompare(String(b.value))
})

const DEFAULT_TZ_OPTION: IOption = { value: '', text: 'Select Timezone...' }
const ALL_TIMEZONE_OPTIONS = [DEFAULT_TZ_OPTION, ...TIMEZONE_OPTIONS]

const AddFacilityModal: React.FC<IAddFacilityModal> = props => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('New Facility')
  const [streetAddress, setStreetAddress] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [facilityType, setFacilityType] = useState('other')
  const [facilityTypeOption, setFacilityTypeOption] = useState<IOption>(
    FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1]
  )
  const [timezone, setTimezone] = useState('')
  const [timezoneOption, setTimezoneOption] = useState<IOption>(DEFAULT_TZ_OPTION)
  const [markerPos, setMarkerPos] = useState<{ lat: number; lng: number } | null>(null)
  const [mapLoading, setMapLoading] = useState(false)
  const mapRef = useRef<IMapboxElement>(null)
  const wasOpenRef = useRef(false)
  useSuppressNotificationSandwich(props.isOpen, NOTIFICATION_SANDWICH_SUPPRESS.FACILITY_GEO)

  useEffect(() => {
    if (props.isOpen && mapRef.current) {
      // Need a short delay to let modal animation finish and container take its final size
      setTimeout(() => {
        mapRef.current?.getMap()?.resize()
      }, 300)
    }
  }, [props.isOpen])

  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = props.isOpen

    if (props.isOpen && !wasOpen) {
      if (props.facilityExisting) {
        setName(props.facilityExisting.name)
        setStreetAddress(props.facilityExisting.street_address || '')
        setCity(props.facilityExisting.city || '')
        setCountry(props.facilityExisting.country || '')
        setPostalCode(props.facilityExisting.postal_code || '')
        setFacilityType(props.facilityExisting.facility_type || 'other')
        setFacilityTypeOption(
          FACILITY_TYPE_OPTIONS.find(o => o.value === props.facilityExisting?.facility_type) ||
            FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1]
        )
        // @ts-ignore
        const tz = props.facilityExisting.timezone || ''
        setTimezone(tz)
        setTimezoneOption(ALL_TIMEZONE_OPTIONS.find(o => o.value === tz) || DEFAULT_TZ_OPTION)
        setMarkerPos(null)
      } else {
        setName('New Facility')
        setStreetAddress('')
        setCity('')
        setCountry('')
        setPostalCode('')
        setFacilityType('other')
        setFacilityTypeOption(FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1])
        setTimezone('')
        setTimezoneOption(DEFAULT_TZ_OPTION)
        setMarkerPos(null)
      }
    }
  }, [props.isOpen])

  const isNameValid = /^[a-zA-Z0-9\s\-]+$/.test(name) && /[a-zA-Z0-9]/.test(name)
  const isSaveDisabled = name === '' || !isNameValid || timezone === ''

  const onSaveClick = async () => {
    try {
      setSaving(true)
      await props.onSave({
        name,
        facility_type: facilityType,
        street_address: streetAddress || undefined,
        city: city || undefined,
        country: country || undefined,
        postal_code: postalCode || undefined,
        // @ts-ignore
        timezone: timezone || undefined,
      })
    } catch (error) {
      console.log('Error creating facility')
    }
    setSaving(false)
  }

  const onCancelClick = () => {
    setName('New Facility')
    setStreetAddress('')
    setCity('')
    setCountry('')
    setPostalCode('')
    setFacilityType('other')
    setFacilityTypeOption(FACILITY_TYPE_OPTIONS[FACILITY_TYPE_OPTIONS.length - 1])
    setTimezone('')
    setTimezoneOption(DEFAULT_TZ_OPTION)
    props.onClose()
  }

  const onTypeSelected = (e: IOption) => {
    setFacilityTypeOption(e)
    setFacilityType(e.value as string)
  }

  const onTimezoneSelected = (e: IOption) => {
    setTimezoneOption(e)
    setTimezone(e.value as string)
  }

  const handleMapClick = async (e: any) => {
    if (!e.lngLat) return
    const { lat, lng } = e.lngLat
    setMarkerPos({ lat, lng })
    setMapLoading(true)
    try {
      const res = await fetch(`https://timeapi.io/api/TimeZone/coordinate?latitude=${lat}&longitude=${lng}`)
      if (res.ok) {
        const data = await res.json()
        if (data.timeZone) {
          setTimezone(data.timeZone)
          const opt = ALL_TIMEZONE_OPTIONS.find(o => o.value === data.timeZone)
          setTimezoneOption(opt || { value: data.timeZone, text: data.timeZone })
        }
      }

      // Reverse geocoding
      const geoRes = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${TOKEN_MAPBOX}`
      )
      if (geoRes.ok) {
        const geoData = await geoRes.json()
        let foundCountry = '',
          foundCity = '',
          foundPostcode = '',
          foundStreet = ''
        if (geoData.features && geoData.features.length > 0) {
          for (const feature of geoData.features) {
            if (!foundStreet && feature.place_type?.includes('address')) {
              foundStreet = (feature.address ? feature.address + ' ' : '') + feature.text
            }
            if (!foundCity && feature.place_type?.includes('place')) foundCity = feature.text
            if (!foundPostcode && feature.place_type?.includes('postcode')) foundPostcode = feature.text
            if (!foundCountry && feature.place_type?.includes('country')) foundCountry = feature.text

            if (feature.context) {
              for (const ctx of feature.context) {
                if (!foundCity && ctx.id.startsWith('place')) foundCity = ctx.text
                if (!foundPostcode && ctx.id.startsWith('postcode')) foundPostcode = ctx.text
                if (!foundCountry && ctx.id.startsWith('country')) foundCountry = ctx.text
              }
            }
          }
        }
        if (foundCountry) setCountry(foundCountry)
        if (foundCity) setCity(foundCity)
        if (foundPostcode) setPostalCode(foundPostcode)
        if (foundStreet) setStreetAddress(foundStreet)
      }
    } catch (err) {
      console.error('Failed to fetch timezone from coordinates', err)
    } finally {
      setMapLoading(false)
    }
  }

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>{props.facilityExisting ? 'Edit facility' : 'Add a new facility'}</div>
      </div>
      <div className={style.mapContainer}>
        <MapboxCommon ref={mapRef} disableRuler disableOverlayControl searchRight={192} onClick={handleMapClick}>
          {markerPos && <Marker longitude={markerPos.lng} latitude={markerPos.lat} color="red" />}
        </MapboxCommon>
      </div>
      <div className={style.contentGrid}>
        <div className={style.leftCol}>
          <div className={style.stack}>
            <TextFieldDescribed
              className={style.textfield}
              labelClassName={style.formLabel}
              small={isMobile}
              description="Facility name"
              value={name}
              placeholder="Input facility name..."
              onChange={e => setName(e.target.value)}
              error="Name must contain letters or numbers"
              hasError={!isNameValid}
            />
            <TextFieldDescribed
              className={style.textfield}
              labelClassName={style.formLabel}
              small={isMobile}
              description="Country"
              value={country}
              placeholder="Input country..."
              onChange={e => setCountry(e.target.value)}
            />
            <TextFieldDescribed
              className={style.textfield}
              labelClassName={style.formLabel}
              small={isMobile}
              description="Street address"
              value={streetAddress}
              placeholder="Input street address..."
              onChange={e => setStreetAddress(e.target.value)}
            />
            {TIMEZONE_OPTIONS.length > 0 && (
              <Dropdown
                className={style.textfield}
                descriptionClassName={style.formLabel}
                currentOption={timezoneOption}
                options={ALL_TIMEZONE_OPTIONS}
                onClickOption={onTimezoneSelected}
                description="Timezone:"
              />
            )}
          </div>
        </div>

        <div className={style.rightCol}>
          <div className={style.stack}>
            <Dropdown
              className={style.textfield}
              descriptionClassName={style.formLabel}
              currentOption={facilityTypeOption}
              options={FACILITY_TYPE_OPTIONS}
              onClickOption={onTypeSelected}
              description="Facility type:"
            />
            <TextFieldDescribed
              className={style.textfield}
              labelClassName={style.formLabel}
              small={isMobile}
              description="City"
              value={city}
              placeholder="Input city..."
              onChange={e => setCity(e.target.value)}
            />
            <TextFieldDescribed
              className={style.textfield}
              labelClassName={style.formLabel}
              small={isMobile}
              description="Postal code"
              value={postalCode}
              placeholder="Input postal code..."
              onChange={e => setPostalCode(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className={style.footer}>
        <Button.Outlined className={style.btn} disabled={saving} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || isSaveDisabled}
          loading={saving}
          onClick={onSaveClick}
        >
          {t('common.save')}
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default AddFacilityModal
